import React from "react";
import {
  Link,
  Navigate,
  useParams
} from "react-router-dom";
import { Container } from "react-bootstrap";

import {
  getProjectById
} from "../../data/projects/projectsData";

import "./ProjectDetailPage.css";

function ProjectDetailPage() {
  const { projectId } = useParams();
  const project = getProjectById(projectId);

  if (!project) {
    return (
      <Navigate
        to="/#projects"
        replace
      />
    );
  }

  const highlights = Array.isArray(project.highlights)
    ? project.highlights
    : [];

  const architecture = Array.isArray(project.architecture)
    ? project.architecture
    : [];

  const technologies = Array.isArray(project.technologies)
    ? project.technologies
    : [];

  const outcomes = Array.isArray(project.outcomes)
    ? project.outcomes
    : [];

  const evidence = Array.isArray(project.evidence)
    ? project.evidence
    : [];

  const remoteAccess =
    project.secureRemoteAccess || null;

  return (
    <main className="project-page">
      <Container className="project-page-container">
        <header className="project-page-header">
          <div className="project-page-meta">
            <span className="project-page-category">
              {project.category}
            </span>

            <span className="project-page-status">
              <span
                className="project-page-status-dot"
                aria-hidden="true"
              />

              {project.status}
            </span>
          </div>

          <h1>{project.title}</h1>

          <p className="project-page-summary">
            {project.summary}
          </p>
        </header>

        {project.coverImage && (
          <figure className="project-page-hero">
            <img
              src={project.coverImage}
              alt={`${project.title} overview`}
            />
          </figure>
        )}

        <div className="project-page-layout">
          <article className="project-page-main">
            <section className="project-page-section">
              <span className="project-page-section-label">
                Project overview
              </span>

              <h2>Overview</h2>

              <p className="project-page-overview">
                {project.overview || project.summary}
              </p>
            </section>

            {highlights.length > 0 && (
              <section className="project-page-section">
                <span className="project-page-section-label">
                  Implementation
                </span>

                <h2>What I built</h2>

                <ul className="project-page-checklist">
                  {highlights.map((highlight, index) => (
                    <li key={`${highlight}-${index}`}>
                      <span
                        className="project-page-check"
                        aria-hidden="true"
                      >
                        ✓
                      </span>

                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {architecture.length > 0 && (
              <section className="project-page-section">
                <span className="project-page-section-label">
                  Technical design
                </span>

                <h2>Architecture and workflow</h2>

                <div className="project-page-steps">
                  {architecture.map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="project-page-step"
                    >
                      <span className="project-page-step-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <p>{item}</p>
                    </div>
                  ))}
                </div>

                {project.showCyberRangeLink && (
                  <Link
                    to="/#cyber-range"
                    className="project-page-inline-link"
                  >
                    View the complete Cyber Range architecture

                    <span aria-hidden="true">→</span>
                  </Link>
                )}
              </section>
            )}

            {remoteAccess && (
              <section className="project-page-section project-page-remote">
                <span className="project-page-section-label">
                  Remote connectivity
                </span>

                <h2>{remoteAccess.title}</h2>

                <p className="project-page-overview">
                  {remoteAccess.description}
                </p>

                {Array.isArray(remoteAccess.flow) &&
                  remoteAccess.flow.length > 0 && (
                    <div className="project-page-flow">
                      {remoteAccess.flow.map((step, index) => (
                        <React.Fragment key={`${step}-${index}`}>
                          <div className="project-page-flow-step">
                            <span>
                              {String(index + 1).padStart(2, "0")}
                            </span>

                            <strong>{step}</strong>
                          </div>

                          {index <
                            remoteAccess.flow.length - 1 && (
                            <span
                              className="project-page-flow-arrow"
                              aria-hidden="true"
                            >
                              →
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                {Array.isArray(remoteAccess.benefits) &&
                  remoteAccess.benefits.length > 0 && (
                    <ul className="project-page-checklist project-page-benefits">
                      {remoteAccess.benefits.map(
                        (benefit, index) => (
                          <li key={`${benefit}-${index}`}>
                            <span
                              className="project-page-check"
                              aria-hidden="true"
                            >
                              ✓
                            </span>

                            <span>{benefit}</span>
                          </li>
                        )
                      )}
                    </ul>
                  )}
              </section>
            )}

            {outcomes.length > 0 && (
              <section className="project-page-section">
                <span className="project-page-section-label">
                  Results
                </span>

                <h2>Key outcomes</h2>

                <ul className="project-page-outcomes">
                  {outcomes.map((outcome, index) => (
                    <li key={`${outcome}-${index}`}>
                      {outcome}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {evidence.length > 0 && (
              <section className="project-page-section project-page-evidence-section">
                <span className="project-page-section-label">
                  Technical evidence
                </span>

                <h2>Implementation evidence</h2>

                <p className="project-page-section-intro">
                  Screenshots from the working environment showing the
                  implementation, configuration, and validation of this
                  project.
                </p>

                <div className="project-page-evidence">
                  {evidence.map((item, index) => (
                    <figure
                      key={`${item.title}-${index}`}
                      className="project-page-evidence-item"
                    >
                      <a
                        href={item.image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-page-evidence-image"
                        aria-label={`Open ${item.title} screenshot`}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          loading="lazy"
                        />
                      </a>

                      <figcaption>
                        <span className="project-page-evidence-number">
                          Evidence{" "}
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <h3>{item.title}</h3>

                        {item.description && (
                          <p>{item.description}</p>
                        )}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            )}
          </article>

          <aside className="project-page-sidebar">
            {technologies.length > 0 && (
              <section className="project-page-sidebar-section">
                <span className="project-page-sidebar-label">
                  Technology
                </span>

                <div className="project-page-technologies">
                  {technologies.map((technology, index) => (
                    <span key={`${technology}-${index}`}>
                      {technology}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <section className="project-page-sidebar-section">
              <span className="project-page-sidebar-label">
                Project
              </span>

              <dl className="project-page-facts">
                <div>
                  <dt>Status</dt>
                  <dd>{project.status}</dd>
                </div>

                <div>
                  <dt>Category</dt>
                  <dd>{project.category}</dd>
                </div>

                {project.role && (
                  <div>
                    <dt>Role</dt>
                    <dd>{project.role}</dd>
                  </div>
                )}

                <div>
                  <dt>Evidence</dt>
                  <dd>
                    {evidence.length}{" "}
                    {evidence.length === 1
                      ? "screenshot"
                      : "screenshots"}
                  </dd>
                </div>
              </dl>
            </section>

            {project.repository && (
              <section className="project-page-sidebar-section">
                <span className="project-page-sidebar-label">
                  Repository
                </span>

                <a
                  href={project.repository}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-page-repository"
                >
                  View GitHub repository

                  <span aria-hidden="true">↗</span>
                </a>
              </section>
            )}
          </aside>
        </div>

        <footer className="project-page-footer-navigation">
          <Link
            to="/#projects"
            className="project-page-footer-back"
          >
            <span aria-hidden="true">←</span>
            Return to all projects
          </Link>

          <Link
            to="/#contact"
            className="project-page-contact"
          >
            Discuss this project

            <span aria-hidden="true">→</span>
          </Link>
        </footer>
      </Container>
    </main>
  );
}

export default ProjectDetailPage;